
import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpParameterCodec, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Configuration } from '@app/common/configuration';
import { apiResponseFormat, BASE_PATH } from '@app/common/variables';
import { ReleaseConfigurationDto } from '@app/apiModules/ReleaseConfigurationDto';
import { ReleaseConfigurationMappingDto } from '../model/addOrUpdateReleaseConfigDto';
import { CustomHttpParameterCodec } from '@app/common/encoder';
import { RoleDto, RoleDtoPagedResultDto } from '@app/apiModules/roleDtoPagedResultDto';
import { Observable, of } from 'rxjs';
import { GetPermissionListResultDto } from '@app/apiModules/getPermissionListResultDto';


@Injectable({
    providedIn: 'root'
})
export class RoleService {
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;
    authServerUrl = environment.API_APP_PATH;

    constructor(private readonly httpClient: HttpClient) {

    }

    /*
     * 
     * @param httpParams
     * @param value
     * @param key
     * @returns
     */

    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }
    /*
     * 
     * @param httpParams
     * @param value
     * @param key
     * @returns
     */
    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key,
                        (value).toISOString().substring(0, 10));
                } else {
                    throw Error("key may not be null if value is Date");
                }
            } else {
                httpParams = this.getHttpParamsFromObject(httpParams, value, key);
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }
    /*
     * 
     * @param httpParams
     * @param value
     * @param key
     * @returns
     */
    private getHttpParamsFromObject(httpParams: HttpParams, value?: any, key?: string) {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        return httpParams;
    }

    /**
     * @description api call to get roles by tenantId
     * @param tenantId 
     * @param requestObj 
     * @param observe 
     * @param reportProgress 
     * @param options 
     */
    public apiAppRoleRolesPost(requestObj: any, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<RoleDtoPagedResultDto>;//NOSONAR
    public apiAppRoleRolesPost(requestObj: any, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<RoleDtoPagedResultDto>>;//NOSONAR
    public apiAppRoleRolesPost(requestObj: any, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<RoleDtoPagedResultDto>>;//NOSONAR
    public apiAppRoleRolesPost(requestObj: any, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {//NOSONAR
        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'text/plain',
                'application/json',
                'text/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }
        return this.httpClient.post<RoleDtoPagedResultDto>(`${this.authServerUrl}/api/app/role/get-roles`, requestObj,
            {
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }


    public apiAppRolePost(roleDto?: RoleDto, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<RoleDto>;
    public apiAppRolePost(roleDto?: RoleDto, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<RoleDto>>;
    public apiAppRolePost(roleDto?: RoleDto, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<RoleDto>>;
    public apiAppRolePost(roleDto?: RoleDto, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {

        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'text/plain',
                'application/json',
                'text/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json-patch+json',
            'application/json',
            'text/json',
            'application/_*+json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }
        return this.httpClient.post<RoleDto>(`${this.authServerUrl}/api/app/role`,
            roleDto,
            {
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
 * @param id 
 * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
 * @param reportProgress flag to report request and response progress.
 */
    public apiAppRoleIdDelete(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept: undefined }): Observable<any>;
    public apiAppRoleIdDelete(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept: undefined }): Observable<HttpResponse<any>>;
    public apiAppRoleIdDelete(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept: undefined }): Observable<HttpEvent<any>>;
    public apiAppRoleIdDelete(id: string, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept: undefined }): Observable<any> {
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling apiAppRoleIdDelete.');
        }

        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }

        return this.httpClient.post<any>(`${this.authServerUrl}/api/app/role/${encodeURIComponent(String(id))}/delete`,
            {
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * @param id 
     * @param tenantId 
     * @param status 
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    /* istanbul ignore next */
    public apiAppRoleIdSetActiveInactivePost(id: string, status?: boolean, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<RoleDto>;
    public apiAppRoleIdSetActiveInactivePost(id: string, status?: boolean, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<RoleDto>>;
    public apiAppRoleIdSetActiveInactivePost(id: string, status?: boolean, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<RoleDto>>;
    public apiAppRoleIdSetActiveInactivePost(id: string, status?: boolean, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling apiAppRoleIdSetActiveInactiveTenantIdPost.');
        }

        let queryParameters = new HttpParams({ encoder: this.encoder });
        if (status !== undefined && status !== null) {
            queryParameters = this.addToHttpParams(queryParameters,
                <any>status, 'status');
        }

        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'text/plain',
                'application/json',
                'text/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }

        return this.httpClient.post<RoleDto>(`${this.authServerUrl}/api/app/role/${encodeURIComponent(String(id))}/set-active-inactive`,
            null,
            {
                params: queryParameters,
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    public apiAppRoleIdGet(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<RoleDto>;
    public apiAppRoleIdGet(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<RoleDto>>;
    public apiAppRoleIdGet(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<RoleDto>>;
    public apiAppRoleIdGet(id: string, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling apiAppRoleIdGet.');
        }

        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'text/plain',
                'application/json',
                'text/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }

        return this.httpClient.get<RoleDto>(`${this.authServerUrl}/api/app/role/${encodeURIComponent(String(id))}`,
            {
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }
    public apiAppRoleIdPut(id: string, roleDto?: RoleDto, skipadminPermissionsforAbp?: boolean, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<RoleDto>;
    public apiAppRoleIdPut(id: string, roleDto?: RoleDto, skipadminPermissionsforAbp?: boolean, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<RoleDto>>;
    public apiAppRoleIdPut(id: string, roleDto?: RoleDto, skipadminPermissionsforAbp?: boolean, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<RoleDto>>;
    public apiAppRoleIdPut(id: string, roleDto?: RoleDto, skipadminPermissionsforAbp?: boolean, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling apiAppRoleIdPut.');
        }

        let headers = this.defaultHeaders;

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'text/plain',
                'application/json',
                'text/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json-patch+json',
            'application/json',
            'text/json',
            'application/_*+json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType: 'text' | 'json' = 'json';
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }

        return this.httpClient.post<RoleDto>(`${this.authServerUrl}/api/app/role/${encodeURIComponent(String(id))}`,
            roleDto,
            {
                responseType: <any>responseType,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }
}
